import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { DividendReportRequest, DividendReportResult } from '@hjci-investorhub-v2/domain';
import { api, useApi } from '../../lib/api';
import { ErrorNotice, Loading, PageHeading } from '../../components/ui/common';
import { PerformanceFilters, type MatrixReference } from './performance-filters';
import { filterParams, readFilters, type MatrixFilters } from './matrix-filters';
import { ReportWarnings } from './report-warnings';
import { DividendReportTable } from './dividend-report-table';

const dividendRequest = (f: MatrixFilters): DividendReportRequest => ({ startDate: f.startDate, endDate: f.endDate, portfolioIds: f.portfolios, subfolioIds: f.subfolios, securityIds: f.shares });
export function AllDividendsPage() {
  const [params, setParams] = useSearchParams(), key = params.toString();
  const filters = useMemo(() => readFilters(new URLSearchParams(key)), [key]);
  const request = dividendRequest(filters);
  const validDates = filters.startDate >= '1900-01-01' && filters.startDate <= filters.endDate;
  const reference = useApi<MatrixReference>('/reports/filter-options');
  const report = useQuery({ queryKey: ['all-dividends', request], queryFn: ({ signal }) => api<DividendReportResult>('/reports/dividends/calculate', { method: 'POST', body: JSON.stringify(request), signal }), enabled: validDates, staleTime: 0, refetchOnMount: 'always' });
  return <><PageHeading title="All dividends" description="Paid dividends during the period and outstanding dividends at the end date." />
    <PerformanceFilters key={key} reportKind="all-dividends" filters={filters} reference={reference.data} onApply={value => {
      const next = filterParams(value); next.delete('dividends');
      if (JSON.stringify(dividendRequest(value)) === JSON.stringify(request)) void report.refetch();
      if (next.toString() !== key) setParams(next);
    }} />
    {reference.error && <ErrorNotice error={reference.error} />}{report.error && <ErrorNotice error={report.error} retry={() => void report.refetch()} />}
    {report.isPending && validDates ? <Loading text="Calculating booked dividend totals…" /> : report.data && <>
      <ReportWarnings reportKind="all-dividends" warnings={report.data.warnings} reference={reference.data} />
      <DividendReportTable key={key} report={report.data} filters={filters} reference={reference.data} refreshing={report.isFetching} onRefresh={() => void report.refetch()} />
      <p className="calculation-footer">Calculation {report.data.calculationVersion} · Booked amounts in AUD · Includes current and sold holdings.</p>
    </>}
  </>;
}
